import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import { useDoUpdate, useOnchainAccount, useOnchainInteractions } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import { formatAmount } from '../utils/format';
import useGenericCall from './useGenericCall';
import { DistributorDetails } from 'impermax-sdk';


export default function useClaimDistributor(distributor: DistributorDetails): [ButtonState, () => Promise<void>] {
  const availableClaimable = useGenericCall(useOnchainAccount(), onchainAccount => onchainAccount.getClaimableIBEX(distributor.claimableAddress));
  const onchainInteractions = useOnchainInteractions();
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const summary = `Claim ${ formatAmount(availableClaimable) } IBEX from ${distributor.name}`;
  
  const claimDistributorState: ButtonState = useMemo(() => {
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [pending]);

  const claimDistributor = useCallback(async (): Promise<void> => {
    if (claimDistributorState !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractions.claimDistributor(distributor.claimableAddress, (hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [claimDistributorState, distributor, summary, addTransaction]);

  return [claimDistributorState, claimDistributor];
}